import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PrivateWrapper } from 'router/PrivateWrapper';
import { pageNames, Path } from 'router/constant';
import { PublicWrapper } from 'router/PublicWrapper';
import DashboardPage from 'pages/dashboard';
import LoginPage from 'pages/login';
import { useAppSelector } from 'hooks/reduxHooks';
import { PageNotFound } from 'shared-components';
import { RootState } from 'redux-setup/store';
import OnboardingPage from 'pages/onboarding';
import { NewSideNavigation as SideNavigation } from 'layouts/NewSideNavigation';
import { useEffect } from 'react';
import PullRequestsPage from 'pages/pull_requests';
import GithubInstallationPage from 'pages/github-installation';
import { GithubAuthLoading } from 'components/github-auth/loading';
import RestrictRoute from './RestrictRoute';
import SettingsPage from 'pages/settings';
import IssuesPage from 'pages/issues';
import WorkflowsPage from 'pages/workflows';
import WorkflowRunStepsPage from 'pages/workflows/workflow_steps';
import SecretsPage from 'pages/secrets';
import DeploymentVersionsPage from 'pages/deployment-versions';
import ClustersPage from 'pages/clusters';
import ClusterDetailsPage from 'pages/clusters/cluster-details';
import ServicesPage from 'pages/services';
import ServicesDetailsPage from 'pages/services/service-details';

interface IProps {
  setTheme: (_theme: boolean) => void;
}

const AppRouter = ({ setTheme }: IProps) => {
  const token = useAppSelector((state: RootState) => state.user.token);
  const theme = useAppSelector((state: RootState) => state.theme.theme);

  const isOnboarding = useAppSelector(
    (state: RootState) => state.user.isOnboarding,
  );

  useEffect(() => {
    setTheme(theme);
    if (theme) {
      document.querySelector('body')?.classList.add('light');
    } else {
      document.querySelector('body')?.classList.remove('light');
    }
  }, [theme]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <Navigate
              to={(() => {
                if (!token) {
                  return Path.LOGIN;
                }
                if (isOnboarding) {
                  return Path.ON_BOARDING;
                }
                return Path.DASHBOARD;
              })()}
            />
          }
        />
        <Route
          path={Path.GITHUB_INSTALLATION}
          element={<GithubInstallationPage />}
        />
        <Route element={<PrivateWrapper auth={{ isAuthenticated: token }} />}>
          <Route
            path={Path.GITHUB_AUTH_LOADING}
            element={<GithubAuthLoading />}
          />
          <Route path={Path.ON_BOARDING} element={<OnboardingPage />} />
          <Route path={Path.APP} element={<SideNavigation />}>
            <Route path={Path.DASHBOARD} element={<DashboardPage />} />
            <Route path={Path.SERVICES} element={<ServicesPage />} />
            <Route
              path={Path.SERVICES_DETAILS}
              element={<ServicesDetailsPage />}
            />
            <Route path={Path.PULL_REQUESTS} element={<PullRequestsPage />} />
            <Route path={Path.WORKFLOWS} element={<WorkflowsPage />} />
            <Route
              path={Path.WORKFLOWS_RUN_STEPS}
              element={<WorkflowRunStepsPage />}
            />
            <Route path={Path.CLUSTERS} element={<ClustersPage />} />
            <Route
              path={Path.CLUSTER_DETAILS}
              element={<ClusterDetailsPage />}
            />
            <Route
              path={Path.DEPLOYMENT_VERSIONS}
              element={<DeploymentVersionsPage />}
            />
            <Route path={Path.ISSUES} element={<IssuesPage />} />
            <Route path={Path.SECRETS} element={<SecretsPage />} />
            <Route
              path={Path.SETTINGS}
              element={
                <RestrictRoute pageName={pageNames.SETTINGS}>
                  <SettingsPage />
                </RestrictRoute>
              }
            />
          </Route>
        </Route>
        <Route element={<PublicWrapper auth={{ isAuthenticated: token }} />}>
          <Route path={Path.LOGIN} element={<LoginPage />} />
        </Route>
        <Route
          path="/*"
          element={
            token ? (
              <PageNotFound />
            ) : (
              <Navigate to={Path.LOGIN} replace={true} />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
